import { useEffect, useRef, useCallback } from 'react';
import { websocketService } from '../../../shared/services/websocketService';
import { bannerService, TelaBanner } from '../../banners/services/bannerService';
import { mergeLayoutStyleOnScreenUpdate } from '../../banners/services/bannerSyncService';
import { Screen } from '../../../shared/types';

interface UseScreenWebSocketOptions {
  screenId: number | null;
  currentScreen: Screen | null;
  currentTelaAPIData: TelaBanner | null;
  onScreenUpdated: (updatedScreen: Screen, updatedTelaAPIData: TelaBanner) => void;
  enabled?: boolean;
}

/**
 * Hook para receber atualizações de tela via WebSocket
 * 
 * Vantagens:
 * - Atualizações em tempo real
 * - Menos requisições HTTP (economia de banda e recursos)
 * - Mais eficiente para o servidor
 */
export const useScreenWebSocket = ({
  screenId,
  currentScreen,
  currentTelaAPIData,
  onScreenUpdated,
  enabled = true
}: UseScreenWebSocketOptions) => {
  const isMountedRef = useRef<boolean>(true);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  // Refs para valores que mudam - evitar re-registrar callback
  const onScreenUpdatedRef = useRef(onScreenUpdated);
  const currentScreenRef = useRef(currentScreen);
  
  // Atualizar refs quando valores mudarem
  useEffect(() => {
    onScreenUpdatedRef.current = onScreenUpdated;
    currentScreenRef.current = currentScreen;
  }, [onScreenUpdated, currentScreen]);

  /**
   * Handler para eventos WebSocket
   */
  // CALLBACK SIMPLES: Recebe dados atualizados e aplica
  const handleScreenUpdate = useCallback((updatedScreen: Screen, updatedTelaAPI: TelaBanner) => {
    if (!isMountedRef.current) return;

    console.log(`[ScreenWebSocket] 🎨 Aplicando atualização da tela ${updatedScreen.id}`);

    const previous = currentScreenRef.current;

    // Preservar parcial: priceBox, panfletoContent (cor de fundo do cartaz, etc.)
    const incomingLayoutStyle = updatedScreen.layoutStyle;
    const mergedLayoutStyle = mergeLayoutStyleOnScreenUpdate(
      previous?.layoutStyle,
      incomingLayoutStyle
    );

    const finalScreen: Screen = {
      ...updatedScreen,
      layoutStyle: mergedLayoutStyle,
      promotions: previous?.promotions || updatedScreen.promotions || [],
      linkedProducts: previous?.linkedProducts || updatedScreen.linkedProducts || []
    };

    // APLICAR IMEDIATAMENTE
    onScreenUpdatedRef.current(finalScreen, updatedTelaAPI);
    console.log(`[ScreenWebSocket] ✅ Tela ${updatedScreen.id} atualizada!`);
  }, []); // Sem dependências - usa refs



  // Configurar monitoramento quando o componente montar ou quando screenId mudar
  // CRÍTICO: Registrar callback IMEDIATAMENTE quando tiver screenId
  // Não esperar por enabled/currentScreen/currentTelaAPIData - o callback faz GET e atualiza tudo
  useEffect(() => {
    isMountedRef.current = true;

    // Registrar callback IMEDIATAMENTE quando tiver screenId
    if (!screenId) {
      console.log(`[ScreenWebSocket] ⏭️ Sem screenId, não iniciando`);
      return;
    }

    console.log(`[ScreenWebSocket] 🚀🚀🚀 INICIANDO REGISTRO DE CALLBACK PARA TELA ${screenId}...`);

    // Conectar e registrar callback DIRETAMENTE aqui, sem depender de useCallback
    const setup = async () => {
      try {
        // Conectar ao WebSocket se não estiver conectado
        if (!websocketService.isConnected()) {
          console.log(`[ScreenWebSocket] Conectando ao SignalR...`);
          await websocketService.connect({
            autoReconnect: true,
            reconnectInterval: 3000,
            maxReconnectAttempts: 10
          });
        }

        // Remover callback anterior se existir
        if (unsubscribeRef.current) {
          console.log(`[ScreenWebSocket] 🗑️ Removendo callback anterior...`);
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
        
        console.log(`[ScreenWebSocket] 📝 Registrando callback direto para tela ${screenId}...`);
        const unsubscribe = websocketService.onScreenUpdate(screenId, handleScreenUpdate);
        unsubscribeRef.current = unsubscribe;
        console.log(`[ScreenWebSocket] ✅✅✅ CALLBACK REGISTRADO PARA TELA ${screenId}!`);

        // Subscribe para atualizações da tela
        await websocketService.subscribeScreen(screenId);
        console.log(`[ScreenWebSocket] ✅ Monitoramento via SignalR iniciado para tela ${screenId}`);
      } catch (error) {
        console.error(`[ScreenWebSocket] ❌ Erro ao configurar WebSocket:`, error);
      }
    };

    setup();

    return () => {
      isMountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (screenId) {
        websocketService.unsubscribeScreen(screenId).catch(console.error);
      }
    };
  }, [screenId, handleScreenUpdate]);

};
